import { emitDataChanged } from "../useDataChanged";
import { advanceCheckpoint, revealTick, type Transition } from "./demoScript";
import { initialDemoState, type DemoState, type ScheduledEvent } from "./demoTypes";

const STORAGE_KEY = "ces.demo.v1";

/** What React subscribes to: the demo being on and where the tour is. */
export interface DemoSnapshot {
  active: boolean;
  chapter: number;
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

interface Persisted {
  active: boolean;
  chapter: number;
  state: DemoState;
  pending: ScheduledEvent[];
}

export interface DemoStoreOptions {
  /** sessionStorage in the browser; null in tests without one. */
  storage?: StorageLike | null;
  now?: () => number;
  /** Called after every change of the sample data (screens re-fetch). */
  onDataChanged?: () => void;
}

/** The demo's single source of truth: sample data, chapter index, and the
 *  script's pending events. Mirrored to sessionStorage on every change so a
 *  reload continues where it was; the tab closing ends the demo. */
export class DemoStore {
  private snapshot: DemoSnapshot = { active: false, chapter: 0 };
  private state: DemoState = initialDemoState();
  private pending: ScheduledEvent[] = [];
  private timers = new Map<ScheduledEvent, ReturnType<typeof setTimeout>>();
  private listeners = new Set<() => void>();
  private readonly storage: StorageLike | null;
  private readonly now: () => number;
  private readonly onDataChanged: () => void;

  constructor(opts: DemoStoreOptions = {}) {
    this.storage = opts.storage ?? null;
    this.now = opts.now ?? (() => Date.now());
    this.onDataChanged = opts.onDataChanged ?? (() => {});
    this.rehydrate();
  }

  // Arrow properties: passed straight to useSyncExternalStore.
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): DemoSnapshot => this.snapshot;

  get active() {
    return this.snapshot.active;
  }
  get chapter() {
    return this.snapshot.chapter;
  }
  getState(): DemoState {
    return this.state;
  }

  /** Chapter 0 (the welcome card) runs before the demo; we begin at 1. */
  start() {
    this.clearTimers();
    this.state = initialDemoState();
    this.setSnapshot({ active: true, chapter: 1 });
    this.persist();
    this.onDataChanged();
  }

  stop() {
    this.clearTimers();
    this.state = initialDemoState();
    this.setSnapshot({ active: false, chapter: 0 });
    this.storage?.removeItem(STORAGE_KEY);
    this.onDataChanged();
  }

  setChapter(chapter: number) {
    if (chapter === this.snapshot.chapter) return;
    this.setSnapshot({ ...this.snapshot, chapter });
    this.persist();
  }

  advanceChapter() {
    this.setChapter(this.snapshot.chapter + 1);
  }

  /** New state now, its events later. Notifies only when the state changed. */
  apply(t: Transition) {
    const changed = t.state !== this.state;
    this.state = t.state;
    for (const s of t.schedule) this.schedule({ at: s.at, event: s.event });
    this.persist();
    if (changed) this.onDataChanged();
  }

  /** A mutation outside the script (mark read, pause, …). */
  update(fn: (state: DemoState) => DemoState) {
    this.apply({ state: fn(this.state), schedule: [] });
  }

  /** Move the scan forward a stage and start its reveal ticks. */
  advanceScan() {
    this.apply(advanceCheckpoint(this.state, this.now()));
  }

  private schedule(entry: ScheduledEvent) {
    this.pending.push(entry);
    this.arm(entry);
  }

  private arm(entry: ScheduledEvent) {
    const delay = Math.max(0, entry.at - this.now());
    this.timers.set(entry, setTimeout(() => this.fire(entry), delay));
  }

  private fire(entry: ScheduledEvent) {
    this.timers.delete(entry);
    this.pending = this.pending.filter((p) => p !== entry);
    if (!this.active) return;
    if (entry.event.type === "reveal-tick") this.apply(revealTick(this.state));
  }

  private clearTimers() {
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
    this.pending = [];
  }

  private setSnapshot(next: DemoSnapshot) {
    this.snapshot = next;
    for (const l of this.listeners) l();
  }

  private persist() {
    if (!this.storage) return;
    if (!this.active) {
      this.storage.removeItem(STORAGE_KEY);
      return;
    }
    const data: Persisted = { active: true, chapter: this.chapter, state: this.state, pending: this.pending };
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      /* quota / disabled storage — best-effort */
    }
  }

  private rehydrate() {
    let raw: string | null = null;
    try {
      raw = this.storage?.getItem(STORAGE_KEY) ?? null;
    } catch {
      raw = null;
    }
    if (!raw) return;
    try {
      const p = JSON.parse(raw) as Persisted;
      if (!p.active) return;
      this.state = { ...initialDemoState(), ...p.state };
      this.snapshot = { active: true, chapter: p.chapter };
      // Overdue events (the tab slept) fire on the next tick, in order.
      for (const e of p.pending ?? []) this.schedule(e);
    } catch {
      this.storage?.removeItem(STORAGE_KEY);
    }
  }
}

export const demoStore = new DemoStore({
  storage: typeof sessionStorage !== "undefined" ? sessionStorage : null,
  onDataChanged: emitDataChanged,
});
